/**
 * Stage 1: normalize raw wall surfaces into cleaned, axis-snapped segments.
 *
 * - scales meters → feet
 * - culls noise segments (shorter than minSurfaceLength)
 * - snaps near-axis walls onto exact horizontal/vertical lines
 * - clusters parallel wall lines within lineClusterTol so collinear walls
 *   share the exact same coordinate (RoomPlan jitters by a few cm)
 */

import { Point, Surface } from '../schemas';
import { ConversionOptions, ConversionReport, FT_PER_M } from './conversionTypes';
import { CleanWall } from './rawGeometry';

export const toFeetPoint = (p: { x: number; y: number }): Point => ({ x: p.x * FT_PER_M, y: p.y * FT_PER_M });

/** Cluster scalar values within tol; returns each value snapped to its cluster's mean. */
export function snapToClusters(values: number[], tol: number): Map<number, number> {
  const sorted = [...new Set(values)].sort((a, b) => a - b);
  const snapped = new Map<number, number>();
  let cluster: number[] = [];
  const flush = () => {
    if (!cluster.length) return;
    const mean = cluster.reduce((s, v) => s + v, 0) / cluster.length;
    for (const v of cluster) snapped.set(v, mean);
    cluster = [];
  };
  for (const v of sorted) {
    if (cluster.length && v - cluster[cluster.length - 1] > tol) flush();
    cluster.push(v);
  }
  flush();
  return snapped;
}

/**
 * Clean one batch of surfaces. Diagonals are kept as-is ("d"); axis walls are
 * snapped flat and their line coordinates clustered across the whole batch.
 */
export function normalizeWalls(
  walls: Surface[],
  opts: ConversionOptions,
  report: ConversionReport,
): CleanWall[] {
  const angleTol = (opts.axisSnapAngleDeg * Math.PI) / 180;

  const pre = walls.flatMap((w): Omit<CleanWall, 'length'>[] => {
    const a = toFeetPoint(w.start), b = toFeetPoint(w.end);
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len < opts.minSurfaceLength) {
      report.culledSurfaceIds.push(w.id);
      return [];
    }
    const ang = Math.atan2(b.y - a.y, b.x - a.x);
    const mod = ((ang % Math.PI) + Math.PI) % Math.PI; // fold to [0, π)
    if (mod < angleTol || Math.PI - mod < angleTol) {
      const y = (a.y + b.y) / 2;
      return [{ id: w.id, a: { x: a.x, y }, b: { x: b.x, y }, axis: 'h' }];
    }
    if (Math.abs(mod - Math.PI / 2) < angleTol) {
      const x = (a.x + b.x) / 2;
      return [{ id: w.id, a: { x, y: a.y }, b: { x, y: b.y }, axis: 'v' }];
    }
    return [{ id: w.id, a, b, axis: 'd' }];
  });

  // Cluster the h-wall y-lines and v-wall x-lines so collinear walls agree exactly.
  const ySnap = snapToClusters(pre.filter(w => w.axis === 'h').map(w => w.a.y), opts.lineClusterTol);
  const xSnap = snapToClusters(pre.filter(w => w.axis === 'v').map(w => w.a.x), opts.lineClusterTol);

  return pre.map(w => {
    if (w.axis === 'h') {
      const y = ySnap.get(w.a.y) ?? w.a.y;
      w = { ...w, a: { x: w.a.x, y }, b: { x: w.b.x, y } };
    } else if (w.axis === 'v') {
      const x = xSnap.get(w.a.x) ?? w.a.x;
      w = { ...w, a: { x, y: w.a.y }, b: { x, y: w.b.y } };
    }
    return { ...w, length: Math.hypot(w.b.x - w.a.x, w.b.y - w.a.y) };
  });
}
